/**
 * Inbound leads page - All leads that came to us (bookings + Meta ads)
 * With integrated dialer for quick calling
 *
 * UX Design Principles:
 * - Scannable at a glance: Name, phone, status, meeting time always visible
 * - Progressive disclosure: Details hidden in accordions by default
 * - Feedback for actions: Toast notifications for status changes
 * - Mobile-first: Large tap targets, no horizontal scroll
 */

import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Calendar,
  Eye,
  Clock,
  ExternalLink,
  CheckCircle,
  Megaphone,
  Phone,
  X,
  DollarSign,
  ThumbsDown,
  MapPin,
  FileText,
  Mail,
  RefreshCw,
  AlertCircle,
  Copy,
  Tag,
  ChevronDown,
  Download,
  ClipboardList,
  MessageSquare,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CallUI } from '@/components/CallUI';
import { SmsConversation } from '@/components/prospects/SmsConversation';
import { ActivityTimeline } from '@/components/prospects/ActivityTimeline';
import { SequenceBadge } from '@/components/prospects/SequenceBadge';
import { useHasDialer, useIsViewer } from '@/lib/auth/AuthProvider';
import { useProspects, useUpdateProspect } from '@/lib/api/hooks/useProspects';
import { exportProspectsCSV } from '@/lib/api/prospects';
import type { Prospect, BrowsingHistoryItem, ProspectStatus, ActivityLogItem } from '@/types/prospect';

// View types for filtering inbound leads
type InboundView = 'upcoming' | 'new' | 'follow_up' | 'pipeline' | 'closed' | 'all';

// Status groups for each view
const VIEW_STATUSES: Record<InboundView, ProspectStatus[] | null> = {
  upcoming: ['scheduled'], // Has meeting_time, sorted by soonest
  new: ['new', 'qualified'],
  follow_up: ['attempted', 'contacted'],
  pipeline: ['interested', 'proposal'],
  closed: ['closed_won', 'closed_lost', 'disqualified', 'not_interested'],
  all: null, // Show all
};

// Status display configuration for visual hierarchy
const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  scheduled: { label: 'Scheduled', className: 'bg-green-100 text-green-800 border-green-300' },
  qualified: { label: 'Qualified', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  attempted: { label: 'Attempted', className: 'bg-orange-100 text-orange-800 border-orange-300' },
  contacted: { label: 'Contacted', className: 'bg-purple-100 text-purple-800 border-purple-300' },
  interested: { label: 'Interested', className: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  proposal: { label: 'Proposal', className: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  closed_won: { label: 'Won', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  closed_lost: { label: 'Lost', className: 'bg-red-100 text-red-800 border-red-300' },
  disqualified: { label: 'Disqualified', className: 'bg-gray-100 text-gray-600 border-gray-300' },
  not_interested: { label: 'Not Interested', className: 'bg-gray-100 text-gray-600 border-gray-300' },
};

function formatMeetingTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return isoString;
  }
}

function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatTimeUntil(isoString: string): { text: string; isUrgent: boolean; isPast: boolean } {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs < 0) {
      // In the past
      const pastMins = Math.floor(-diffMs / 60000);
      const pastHours = Math.floor(-diffMs / 3600000);
      if (pastMins < 60) return { text: `${pastMins}m ago`, isUrgent: false, isPast: true };
      if (pastHours < 24) return { text: `${pastHours}h ago`, isUrgent: false, isPast: true };
      return { text: 'past', isUrgent: false, isPast: true };
    }

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Urgent if within 2 hours
    const isUrgent = diffHours < 2;

    if (diffMins < 60) return { text: `in ${diffMins}m`, isUrgent, isPast: false };
    if (diffHours < 24) return { text: `in ${diffHours}h`, isUrgent, isPast: false };
    if (diffDays === 1) return { text: 'tomorrow', isUrgent: false, isPast: false };
    return { text: `in ${diffDays}d`, isUrgent: false, isPast: false };
  } catch {
    return { text: '', isUrgent: false, isPast: false };
  }
}

function formatTimeOnPage(seconds?: number): string {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatPageViewTime(isoString?: string): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (isYesterday) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  } catch {
    return '';
  }
}

// Deduplicate and process browsing history
interface ProcessedPageView {
  page: string;
  count: number;
  firstTimestamp?: string;
  lastTimestamp?: string;
  totalTimeOnPage: number;
  maxScrollDepth: number;
}

function processHistoryForDisplay(history: BrowsingHistoryItem[]): ProcessedPageView[] {
  const pageMap = new Map<string, ProcessedPageView>();

  history.forEach((item) => {
    const existing = pageMap.get(item.page);
    if (existing) {
      existing.count++;
      existing.totalTimeOnPage += item.time_on_page || 0;
      existing.maxScrollDepth = Math.max(existing.maxScrollDepth, item.scroll_depth || 0);
      if (item.timestamp) {
        if (!existing.firstTimestamp || item.timestamp < existing.firstTimestamp) {
          existing.firstTimestamp = item.timestamp;
        }
        if (!existing.lastTimestamp || item.timestamp > existing.lastTimestamp) {
          existing.lastTimestamp = item.timestamp;
        }
      }
    } else {
      pageMap.set(item.page, {
        page: item.page,
        count: 1,
        firstTimestamp: item.timestamp,
        lastTimestamp: item.timestamp,
        totalTimeOnPage: item.time_on_page || 0,
        maxScrollDepth: item.scroll_depth || 0,
      });
    }
  });

  // Sort by most recent first
  return Array.from(pageMap.values()).sort((a, b) => {
    if (a.lastTimestamp && b.lastTimestamp) {
      return new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime();
    }
    return 0;
  });
}

// Activity type labels for display
const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  booking_created: 'Booked',
  booking_rescheduled: 'Rescheduled',
  booking_cancelled: 'Cancelled',
  status_changed: 'Status Changed',
  call_made: 'Called',
  email_sent: 'Email Sent',
  note_added: 'Note Added',
};

// Lost reason options
const LOST_REASONS = [
  { value: 'pricing', label: 'Pricing too high' },
  { value: 'timing', label: 'Bad timing' },
  { value: 'competitor', label: 'Went with competitor' },
  { value: 'no_budget', label: 'No budget' },
  { value: 'no_response', label: 'No response' },
  { value: 'not_a_fit', label: 'Not a good fit' },
  { value: 'other', label: 'Other' },
];

// Close Won Modal
interface CloseWonModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (amount: number) => void;
  prospectName: string;
  isLoading?: boolean;
}

function CloseWonModal({ open, onClose, onConfirm, prospectName, isLoading }: CloseWonModalProps) {
  const [amount, setAmount] = useState('');

  const handleSubmit = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) return;
    onConfirm(numAmount);
    setAmount('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Close Won - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Enter the deal amount to mark this lead as closed won.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="amount">Deal Amount ($)</Label>
          <Input
            id="amount"
            type="number"
            placeholder="e.g., 5000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-2"
            min="0"
            step="0.01"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!amount || parseFloat(amount) <= 0 || isLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? 'Saving...' : 'Close Won'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Close Lost Modal
interface CloseLostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes?: string) => void;
  prospectName: string;
  isLoading?: boolean;
}

function CloseLostModal({ open, onClose, onConfirm, prospectName, isLoading }: CloseLostModalProps) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    if (!reason) return;
    onConfirm(reason, notes || undefined);
    setReason('');
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ThumbsDown className="h-5 w-5 text-red-600" />
            Close Lost - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Select a reason why this deal was lost.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {LOST_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Additional context..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || isLoading}
            variant="destructive"
          >
            {isLoading ? 'Saving...' : 'Close Lost'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Disqualify Modal
interface DisqualifyModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  prospectName: string;
  isLoading?: boolean;
}

function DisqualifyModal({ open, onClose, onConfirm, prospectName, isLoading }: DisqualifyModalProps) {
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    onConfirm(notes);
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <X className="h-5 w-5 text-gray-600" />
            Disqualify - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Add notes explaining why this lead is not a fit (for reporting).
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="disqualify-notes">Qualification Notes</Label>
          <Textarea
            id="disqualify-notes"
            placeholder="e.g., Wrong industry, too small, not decision maker..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-2"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading} variant="secondary">
            {isLoading ? 'Saving...' : 'Disqualify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Meeting Outcome Modal (for agency setter/closer workflow)
interface MeetingOutcomeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    setter?: string;
    attended_meeting?: boolean;
    no_show_answered_dial?: boolean;
    no_show_no_answers?: boolean;
    qualification_label?: string;
    next_steps?: string;
  }) => void;
  prospectName: string;
  isLoading?: boolean;
  initialData?: {
    setter?: string;
    attended_meeting?: boolean;
    no_show_answered_dial?: boolean;
    no_show_no_answers?: boolean;
    qualification_label?: string;
    next_steps?: string;
  };
}

function MeetingOutcomeModal({ open, onClose, onSave, prospectName, isLoading, initialData }: MeetingOutcomeModalProps) {
  const [setter, setSetter] = useState(initialData?.setter || '');
  const [attended, setAttended] = useState<string>(
    initialData?.attended_meeting === true ? 'yes' : initialData?.attended_meeting === false ? 'no' : ''
  );
  const [noShowAnswered, setNoShowAnswered] = useState(initialData?.no_show_answered_dial || false);
  const [noShowNoAnswers, setNoShowNoAnswers] = useState(initialData?.no_show_no_answers || false);
  const [qualLabel, setQualLabel] = useState(initialData?.qualification_label || '');
  const [nextSteps, setNextSteps] = useState(initialData?.next_steps || '');

  const handleSubmit = () => {
    onSave({
      ...(setter ? { setter } : {}),
      attended_meeting: attended === 'yes' ? true : attended === 'no' ? false : undefined,
      ...(attended === 'no' ? {
        no_show_answered_dial: noShowAnswered,
        no_show_no_answers: noShowNoAnswers,
      } : {}),
      ...(qualLabel ? { qualification_label: qualLabel } : {}),
      ...(nextSteps ? { next_steps: nextSteps } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-blue-600" />
            Meeting Outcome - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Record meeting attendance and qualification details.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="setter">Setter</Label>
            <Input
              id="setter"
              placeholder="Who set this lead?"
              value={setter}
              onChange={(e) => setSetter(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Attended Meeting?</Label>
            <Select value={attended} onValueChange={setAttended}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No (No-show)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {attended === 'no' && (
            <div className="space-y-3 pl-4 border-l-2 border-muted">
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant={noShowAnswered ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNoShowAnswered(!noShowAnswered)}
                >
                  {noShowAnswered ? 'Answered follow-up dial' : 'Answered dial?'}
                </Button>
                <Button
                  type="button"
                  variant={noShowNoAnswers ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNoShowNoAnswers(!noShowNoAnswers)}
                >
                  {noShowNoAnswers ? 'No answers on follow-up' : 'No answers?'}
                </Button>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="qual-label">Qualification</Label>
            <Input
              id="qual-label"
              placeholder="e.g., Qualified, Qualified - ish, Not a fit"
              value={qualLabel}
              onChange={(e) => setQualLabel(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="next-steps">Next Steps</Label>
            <Textarea
              id="next-steps"
              placeholder="What happens next?"
              value={nextSteps}
              onChange={(e) => setNextSteps(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Outcome'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BrowsingHistory({ history }: { history: BrowsingHistoryItem[] }) {
  const [showAll, setShowAll] = useState(false);

  if (!history || history.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">No browsing history available</p>
    );
  }

  const processedPages = processHistoryForDisplay(history);
  const displayLimit = 5;
  const hasMore = processedPages.length > displayLimit;
  const pagesToShow = showAll ? processedPages : processedPages.slice(0, displayLimit);

  return (
    <div className="space-y-2">
      {pagesToShow.map((item, index) => (
        <div key={index} className="flex items-start gap-2 text-sm">
          <Eye className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">
                {item.page}
                {item.count > 1 && (
                  <span className="text-muted-foreground ml-1">({item.count}x)</span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {item.lastTimestamp && (
                <span>{formatPageViewTime(item.lastTimestamp)}</span>
              )}
              {item.totalTimeOnPage > 0 && (
                <span>{formatTimeOnPage(item.totalTimeOnPage)}</span>
              )}
              {item.maxScrollDepth > 0 && (
                <span className="text-muted-foreground/70">{item.maxScrollDepth}% scroll</span>
              )}
            </div>
          </div>
        </div>
      ))}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-xs text-primary hover:underline"
        >
          {showAll ? 'Show less' : `Show all ${processedPages.length} pages`}
        </button>
      )}
    </div>
  );
}

// Notes & Activity Display Component
interface NotesDisplayProps {
  activityLog?: ActivityLogItem[];
  notes?: string;
  qualificationNotes?: string;
}

function NotesDisplay({ activityLog, notes, qualificationNotes }: NotesDisplayProps) {
  const hasActivity = activityLog && activityLog.length > 0;
  const hasNotes = notes && notes.trim().length > 0;

  if (!hasActivity && !hasNotes && !qualificationNotes) {
    return <p className="text-sm text-muted-foreground italic">No activity or notes</p>;
  }

  return (
    <div className="space-y-3 text-sm">
      {/* Activity Timeline (structured system events) */}
      {hasActivity && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Activity</p>
          <div className="space-y-2 pl-2 border-l-2 border-muted">
            {activityLog.map((event, index) => (
              <div key={index} className="text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground/70">
                    {formatPageViewTime(event.timestamp)}
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {ACTIVITY_TYPE_LABELS[event.type] || event.type}
                  </Badge>
                </div>
                <p className="mt-0.5">{event.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Notes (clean, user-entered text) */}
      {hasNotes && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</p>
          <p className="text-muted-foreground whitespace-pre-wrap">{notes}</p>
        </div>
      )}

      {/* Qualification Notes */}
      {qualificationNotes && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Qualification</p>
          <p className="text-muted-foreground italic">{qualificationNotes}</p>
        </div>
      )}
    </div>
  );
}

// Email Activity Item - shows subject, recipient, approach, and expandable body
function EmailActivityItem({ email }: { email: ActivityLogItem }) {
  const [showBody, setShowBody] = useState(false);
  const hasBody = !!email.metadata?.body;

  return (
    <div className="p-2 rounded-md bg-muted/30 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate">
          {email.metadata?.subject || email.description}
        </span>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatPageViewTime(email.timestamp)}
        </span>
      </div>
      {email.metadata?.to && (
        <span className="text-xs text-muted-foreground block">
          To: {email.metadata.to}
        </span>
      )}
      <div className="flex items-center gap-2">
        {email.metadata?.approach && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {email.metadata.approach}
          </Badge>
        )}
        {email.metadata?.template && (
          <span className="text-xs text-muted-foreground">
            Draft: {email.metadata.template}
          </span>
        )}
      </div>
      {hasBody && (
        <>
          <button
            onClick={() => setShowBody(!showBody)}
            className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${showBody ? 'rotate-180' : ''}`} />
            {showBody ? 'Hide email' : 'Show email'}
          </button>
          {showBody && (
            <div
              className="mt-2 p-3 rounded border bg-background text-xs leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: email.metadata.body }}
            />
          )}
        </>
      )}
    </div>
  );
}

// Status Badge Component with color coding
function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || {
    label: status.replace(/_/g, ' '),
    className: 'bg-gray-100 text-gray-600 border-gray-300'
  };

  return (
    <Badge variant="outline" className={`${config.className} capitalize`}>
      {config.label}
    </Badge>
  );
}

interface InboundLeadCardProps {
  prospect: Prospect;
  onCall?: (prospectId: string, phone: string, name?: string) => void;
  onStatusChange?: (prospectId: string, status: ProspectStatus, extra?: Record<string, unknown>) => void;
  isSelected?: boolean;
  isUpdating?: boolean;
  isViewer?: boolean;
}

function InboundLeadCard({ prospect, onCall, onStatusChange, isSelected, isUpdating, isViewer }: InboundLeadCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCloseWon, setShowCloseWon] = useState(false);
  const [showCloseLost, setShowCloseLost] = useState(false);
  const [showDisqualify, setShowDisqualify] = useState(false);
  const [showMeetingOutcome, setShowMeetingOutcome] = useState(false);

  // Use base fields (not extra_fields) for attribution data
  const utmSource = prospect.utm_source;
  const utmCampaign = prospect.utm_campaign;
  const utmMedium = prospect.utm_medium;
  const hasBooking = !!prospect.meeting_time;
  const isMetaLead = prospect.source === 'meta_lead_ad';
  const displayName = prospect.name || prospect.company_name || 'Unknown';
  const hasBrowsingHistory = prospect.browsing_history && prospect.browsing_history.length > 0;
  const hasUtmData = utmSource || utmCampaign || utmMedium;
  const hasActivityOrNotes = prospect.activity_log?.length || prospect.notes || prospect.qualification_notes;
  const emailActivities = useMemo(() =>
    (prospect.activity_log || []).filter(a => a.type === 'email_sent'),
    [prospect.activity_log]
  );
  const hasEmails = emailActivities.length > 0;
  const hasMetaFields = isMetaLead && (prospect.meta_ad_name || prospect.meta_campaign_name || prospect.meta_platform || prospect.meta_lead_id || prospect.meta_custom_responses || prospect.meta_ad_image_url);

  // Attribution data (now using base fields)
  const landingPage = prospect.landing_page;
  const bookingPage = prospect.booking_page;
  const fbclid = prospect.fbclid;
  const gclid = prospect.gclid;
  const referrer = prospect.referrer;
  const deviceType = prospect.device_type;
  const timezone = prospect.timezone;
  const hasAttributionData = hasUtmData || landingPage || bookingPage || fbclid || gclid || referrer || deviceType;

  // Calculate session stats from browsing history
  const sessionStats = useMemo(() => {
    if (!prospect.browsing_history?.length) return null;
    const pageCount = prospect.browsing_history.length;
    const totalTime = prospect.browsing_history.reduce((sum, p) => sum + (p.time_on_page || 0), 0);
    return { pageCount, totalTime };
  }, [prospect.browsing_history]);

  // Calculate meeting duration if we have both times
  const meetingDuration = useMemo(() => {
    if (!prospect.meeting_time || !prospect.meeting_end_time) return null;
    try {
      const start = new Date(prospect.meeting_time);
      const end = new Date(prospect.meeting_end_time);
      return Math.round((end.getTime() - start.getTime()) / 60000); // minutes
    } catch {
      return null;
    }
  }, [prospect.meeting_time, prospect.meeting_end_time]);

  // Copy phone to clipboard
  const handleCopyPhone = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (prospect.phone && prospect.phone !== 'pending') {
      try {
        await navigator.clipboard.writeText(prospect.phone);
        toast.success('Phone number copied to clipboard');
      } catch {
        toast.error('Failed to copy phone number');
      }
    }
  };

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (prospect.phone && prospect.phone !== 'pending' && onCall) {
      onCall(prospect.id, prospect.phone, displayName);
      toast.info(`Preparing to call ${displayName}`);
    }
  };

  // Determine which workflow buttons to show based on current status
  const status = prospect.status as ProspectStatus;
  const isClosedOrDisqualified = ['closed_won', 'closed_lost', 'disqualified', 'not_interested'].includes(status);
  const canQualify = status === 'new';
  const canMarkProposal = ['qualified', 'contacted', 'interested'].includes(status);
  const canClose = ['proposal', 'interested', 'contacted', 'qualified'].includes(status);
  // Show call outcome buttons for non-closed leads
  const canRecordCallOutcome = !isClosedOrDisqualified && !['proposal', 'interested'].includes(status);
  // Show "Interested" button for leads that have been contacted
  const canMarkInterested = ['qualified', 'contacted', 'attempted'].includes(status);

  // Check if phone is valid (not pending, empty, N/A, etc.)
  const hasValidPhone = prospect.phone &&
    prospect.phone !== 'pending' &&
    prospect.phone.trim() !== '' &&
    prospect.phone.toLowerCase() !== 'n/a';

  const handleQuickStatusChange = (newStatus: ProspectStatus) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, newStatus);
    }
  };

  const handleCloseWon = (amount: number) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, 'closed_won', {
        closed_amount: amount,
        closed_at: new Date().toISOString(),
      });
      setShowCloseWon(false);
    }
  };

  const handleCloseLost = (reason: string, notes?: string) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, 'closed_lost', {
        lost_reason: reason,
        closed_at: new Date().toISOString(),
        ...(notes ? { qualification_notes: notes } : {}),
      });
      setShowCloseLost(false);
    }
  };

  const handleDisqualify = (notes: string) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, 'disqualified', {
        qualification_notes: notes,
      });
      setShowDisqualify(false);
    }
  };

  const handleMeetingOutcome = (data: Record<string, unknown>) => {
    if (onStatusChange) {
      // Pass the current status (don't change it) with the extra agency fields
      onStatusChange(prospect.id, prospect.status as ProspectStatus, data);
      setShowMeetingOutcome(false);
    }
  };

  // Format arrival time
  const arrivedTime = prospect.created_at ? formatRelativeTime(prospect.created_at) : '';
  const meetingInfo = prospect.meeting_time ? formatTimeUntil(prospect.meeting_time) : null;

  // Attendance indicator state: attended | no_show | pending (past, unset) | null (no meeting or future)
  const attendanceIndicator = useMemo((): 'attended' | 'no_show' | 'pending' | null => {
    if (!prospect.meeting_time) return null;
    if (prospect.attended_meeting === true) return 'attended';
    if (prospect.attended_meeting === false) return 'no_show';
    if (meetingInfo?.isPast) return 'pending';
    return null;
  }, [prospect.meeting_time, prospect.attended_meeting, meetingInfo?.isPast]);

  // Determine which accordion sections have content
  const accordionSections: string[] = ['sms', 'activity'];
  if (hasEmails) accordionSections.push('emails');
  if (hasBrowsingHistory) accordionSections.push('pages');
  if (hasAttributionData) accordionSections.push('source');
  if (hasMetaFields) accordionSections.push('meta');
  if (hasActivityOrNotes) accordionSections.push('notes');

  return (
    <Card className={`group ${isSelected ? 'ring-2 ring-primary' : ''} ${isUpdating ? 'opacity-70' : ''}`}>
      {/* Compact Header - Always Visible */}
      <CardHeader
        className="pb-2 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between gap-2">
          {/* Left side: Name, email, status */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold truncate">{displayName}</h3>
              <StatusBadge status={status} />
              <SequenceBadge prospectId={prospect.id} />
            </div>
            {prospect.email && (
              <p className="text-sm text-muted-foreground truncate">{prospect.email}</p>
            )}
          </div>

          {/* Right side: Expand toggle and call button on hover */}
          <div className="flex items-center gap-2">
            {/* Call button - subtle when collapsed, visible on hover or when expanded */}
            {!isViewer && hasValidPhone && !isClosedOrDisqualified && onCall && (
              <Button
                size="sm"
                onClick={handleCall}
                className={`h-8 px-3 transition-all duration-150 ${
                  isExpanded
                    ? 'bg-green-600 hover:bg-green-700 opacity-100'
                    : 'bg-green-600/70 hover:bg-green-700 opacity-40 hover:opacity-100'
                }`}
                disabled={isUpdating}
              >
                <Phone className="h-3 w-3 mr-1" />
                Call
              </Button>
            )}
            {/* Expand/collapse toggle */}
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {/* Meeting time badge - always visible if exists */}
        {prospect.meeting_time && meetingInfo && (
          <div className={`flex items-center gap-2 text-xs mt-2 p-2 rounded-md ${
            meetingInfo.isPast
              ? 'bg-gray-100 text-gray-600'
              : meetingInfo.isUrgent
                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                : 'bg-muted/50'
          }`}>
            <Clock className={`h-3 w-3 flex-shrink-0 ${meetingInfo.isUrgent ? 'animate-pulse' : ''}`} />
            <span className="font-medium">{formatMeetingTime(prospect.meeting_time)}</span>
            <span className={`${meetingInfo.isPast ? 'text-gray-500' : 'text-muted-foreground'}`}>
              ({meetingInfo.text})
            </span>
          </div>
        )}

        {/* Attendance indicator — visible without expanding card */}
        {attendanceIndicator && (
          <div className="flex items-center gap-1 mt-1">
            {attendanceIndicator === 'attended' && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Attended
              </Badge>
            )}
            {attendanceIndicator === 'no_show' && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                <X className="h-3 w-3 mr-1" />
                No-show
              </Badge>
            )}
            {attendanceIndicator === 'pending' && !isViewer && onStatusChange && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs px-2 text-green-700 border-green-300 hover:bg-green-50"
                  disabled={isUpdating}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(prospect.id, prospect.status as ProspectStatus, { attended_meeting: true });
                  }}
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Attended
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs px-2 text-red-700 border-red-300 hover:bg-red-50"
                  disabled={isUpdating}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(prospect.id, prospect.status as ProspectStatus, { attended_meeting: false });
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  No-show
                </Button>
              </>
            )}
            {attendanceIndicator === 'pending' && isViewer && (
              <Badge variant="outline" className="text-gray-500 border-gray-300 text-xs">
                <Clock className="h-3 w-3 mr-1" />
                Pending
              </Badge>
            )}
          </div>
        )}
      </CardHeader>

      {/* Expanded Content */}
      {isExpanded && (
        <CardContent className="pt-2 space-y-3">
          {/* Contact details and source info */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {/* Phone */}
            {hasValidPhone ? (
              <button
                onClick={handleCopyPhone}
                className="font-mono text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                title="Click to copy"
              >
                <Phone className="h-3 w-3" />
                {prospect.phone}
                <Copy className="h-3 w-3" />
              </button>
            ) : (
              <span className="text-muted-foreground italic flex items-center gap-1">
                <Phone className="h-3 w-3" />
                No phone
              </span>
            )}

            {/* Source badge */}
            {hasBooking && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Calendar className="h-3 w-3 mr-1" />
                Booked
              </Badge>
            )}
            {isMetaLead && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 cursor-help">
                      <Megaphone className="h-3 w-3 mr-1" />
                      {prospect.meta_campaign_name
                        ? `Meta • ${prospect.meta_campaign_name.length > 30 ? prospect.meta_campaign_name.slice(0, 30) + '…' : prospect.meta_campaign_name}`
                        : prospect.meta_campaign_id
                        ? `Meta • Campaign: ${prospect.meta_campaign_id}`
                        : 'Meta'}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1 text-xs">
                      {prospect.meta_campaign_name && <div><strong>Campaign:</strong> {prospect.meta_campaign_name}</div>}
                      {prospect.meta_ad_name && <div><strong>Ad:</strong> {prospect.meta_ad_name}</div>}
                      {prospect.meta_adset_name && <div><strong>Ad Set:</strong> {prospect.meta_adset_name}</div>}
                      {prospect.meta_platform && (
                        <div><strong>Platform:</strong> {prospect.meta_platform === 'fb' ? 'Facebook' : prospect.meta_platform === 'ig' ? 'Instagram' : prospect.meta_platform}</div>
                      )}
                      {!prospect.meta_campaign_name && !prospect.meta_ad_name && !prospect.meta_adset_name && !prospect.meta_platform && (
                        <div className="text-muted-foreground">No attribution data available</div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Arrival time */}
            {arrivedTime && (
              <span className="text-xs text-muted-foreground">Arrived {arrivedTime}</span>
            )}
          </div>

          {/* No meeting warning for Meta leads */}
          {!prospect.meeting_time && isMetaLead && (
            <div className="flex items-center gap-2 text-sm bg-yellow-50 text-yellow-700 p-2 rounded-md border border-yellow-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>No meeting booked - needs follow-up</span>
            </div>
          )}

          {/* Primary Actions */}
          {prospect.website && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="h-9" asChild>
                <a href={prospect.website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Research
                </a>
              </Button>
            </div>
          )}

          {/* Closed deal info */}
          {status === 'closed_won' && prospect.closed_amount && (
            <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 p-2 rounded-md border border-emerald-200">
              <DollarSign className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">Won: ${prospect.closed_amount.toLocaleString()}</span>
            </div>
          )}
          {status === 'closed_lost' && prospect.lost_reason && (
            <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 p-2 rounded-md border border-red-200">
              <ThumbsDown className="h-4 w-4 flex-shrink-0" />
              <span>Lost: {LOST_REASONS.find(r => r.value === prospect.lost_reason)?.label || prospect.lost_reason}</span>
            </div>
          )}

          {/* Agency tracking fields (when present) */}
          {(prospect.setter || prospect.qualification_label || prospect.next_steps || prospect.attended_meeting === true || prospect.attended_meeting === false) && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {prospect.setter && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Setter: {prospect.setter}
                </Badge>
              )}
              {prospect.qualification_label && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                  {prospect.qualification_label}
                </Badge>
              )}
              {prospect.attended_meeting === true && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Attended
                </Badge>
              )}
              {prospect.attended_meeting === false && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  No-show
                  {prospect.no_show_answered_dial && ' (answered dial)'}
                  {prospect.no_show_no_answers && ' (no answers)'}
                </Badge>
              )}
              {prospect.next_steps && (
                <span className="text-muted-foreground">Next: {prospect.next_steps}</span>
              )}
            </div>
          )}

          {/* Collapsible Details - Accordions */}
          {accordionSections.length > 0 && (
            <Accordion type="multiple" className="w-full">
              {/* SMS Messages */}
              <AccordionItem value="sms" className="border-none">
                <AccordionTrigger className="py-2 text-sm hover:no-underline">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    SMS Messages
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <SmsConversation prospectId={prospect.id} isViewer={isViewer} />
                </AccordionContent>
              </AccordionItem>

              {/* Activity Timeline */}
              <AccordionItem value="activity" className="border-none">
                <AccordionTrigger className="py-2 text-sm hover:no-underline">
                  <span className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    Activity Timeline
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ActivityTimeline prospectId={prospect.id} />
                </AccordionContent>
              </AccordionItem>

              {/* Emails Sent */}
              {hasEmails && (
                <AccordionItem value="emails" className="border-none">
                  <AccordionTrigger className="py-2 text-sm hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      Emails Sent ({emailActivities.length})
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 text-sm">
                      {emailActivities.map((email, index) => (
                        <EmailActivityItem key={index} email={email} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Browsing History */}
              {hasBrowsingHistory && (
                <AccordionItem value="pages" className="border-none">
                  <AccordionTrigger className="py-2 text-sm hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      Pages Viewed ({prospect.browsing_history?.length})
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <BrowsingHistory history={prospect.browsing_history || []} />
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* UTM / Source Details */}
              {hasAttributionData && (
                <AccordionItem value="source" className="border-none">
                  <AccordionTrigger className="py-2 text-sm hover:no-underline">
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      Source & Attribution
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 text-sm">
                      {/* UTM Parameters */}
                      {utmSource && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Source:</span>
                          <Badge variant="secondary" className="font-mono text-xs">{utmSource}</Badge>
                        </div>
                      )}
                      {utmMedium && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Medium:</span>
                          <Badge variant="secondary" className="font-mono text-xs">{utmMedium}</Badge>
                        </div>
                      )}
                      {utmCampaign && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Campaign:</span>
                          <Badge variant="secondary" className="font-mono text-xs">{utmCampaign}</Badge>
                        </div>
                      )}

                      {/* Page Journey */}
                      {referrer && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Referrer:</span>
                          <span className="font-mono text-xs truncate">{referrer}</span>
                        </div>
                      )}
                      {landingPage && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Landing:</span>
                          <span className="font-mono text-xs truncate">{landingPage}</span>
                        </div>
                      )}
                      {bookingPage && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Booked on:</span>
                          <span className="font-mono text-xs truncate">{bookingPage}</span>
                        </div>
                      )}

                      {/* Session Stats */}
                      {sessionStats && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Session:</span>
                          <span className="text-xs">
                            {sessionStats.pageCount} pages, {formatTimeOnPage(sessionStats.totalTime)}
                          </span>
                        </div>
                      )}

                      {/* Device & Context */}
                      {deviceType && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Device:</span>
                          <Badge variant="outline" className="text-xs capitalize">{deviceType}</Badge>
                        </div>
                      )}
                      {timezone && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Timezone:</span>
                          <span className="text-xs">{timezone}</span>
                        </div>
                      )}
                      {meetingDuration && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Meeting:</span>
                          <span className="text-xs">{meetingDuration} min</span>
                        </div>
                      )}

                      {/* Click IDs (collapsed by default - mostly for debugging) */}
                      {(fbclid || gclid) && (
                        <div className="pt-2 border-t border-muted mt-2">
                          <p className="text-xs text-muted-foreground mb-1">Click IDs</p>
                          {fbclid && (
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground w-20">Facebook:</span>
                              <span className="font-mono text-xs truncate max-w-[200px]" title={fbclid}>{fbclid.slice(0, 20)}...</span>
                            </div>
                          )}
                          {gclid && (
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground w-20">Google:</span>
                              <span className="font-mono text-xs truncate max-w-[200px]" title={gclid}>{gclid.slice(0, 20)}...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Meta Lead Info */}
              {hasMetaFields && (
                <AccordionItem value="meta" className="border-none">
                  <AccordionTrigger className="py-2 text-sm hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      Meta Lead Info
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 text-sm">
                      {/* Ad Creative Preview */}
                      {(prospect.meta_ad_image_url || prospect.meta_ad_thumbnail_url) && (
                        <div className="mb-3">
                          <img
                            src={prospect.meta_ad_image_url || prospect.meta_ad_thumbnail_url}
                            alt={prospect.meta_ad_title || prospect.meta_ad_name || 'Ad creative'}
                            className="rounded-md border max-h-48 w-auto object-contain"
                          />
                          {prospect.meta_ad_title && (
                            <p className="text-xs font-medium mt-1">{prospect.meta_ad_title}</p>
                          )}
                          {prospect.meta_ad_body && (
                            <p className="text-xs text-muted-foreground mt-0.5">{prospect.meta_ad_body}</p>
                          )}
                        </div>
                      )}
                      {prospect.meta_campaign_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Campaign:</span>
                          <span>{prospect.meta_campaign_name}</span>
                        </div>
                      )}
                      {prospect.meta_adset_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Ad Set:</span>
                          <span>{prospect.meta_adset_name}</span>
                        </div>
                      )}
                      {prospect.meta_ad_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Ad:</span>
                          <span>{prospect.meta_ad_name}</span>
                        </div>
                      )}
                      {prospect.meta_platform && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-20">Platform:</span>
                          <Badge variant="secondary" className="text-xs capitalize">
                            {prospect.meta_platform === 'ig' ? 'Instagram' : prospect.meta_platform === 'fb' ? 'Facebook' : prospect.meta_platform}
                          </Badge>
                        </div>
                      )}
                      {prospect.meta_custom_responses && Object.keys(prospect.meta_custom_responses).length > 0 && (
                        <div className="pt-2 border-t border-muted mt-2 space-y-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Form Responses</p>
                          {Object.entries(prospect.meta_custom_responses).map(([question, answer]) => (
                            <div key={question} className="flex items-start gap-2">
                              <span className="text-muted-foreground capitalize text-xs">{question.replace(/_/g, ' ').replace(/\?$/, '')}:</span>
                              <span className="capitalize text-xs font-medium">{answer.replace(/_/g, ' ')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {prospect.meta_lead_id && (
                        <div className="flex items-center gap-2 pt-2 border-t border-muted mt-2">
                          <span className="text-muted-foreground w-20">Lead ID:</span>
                          <span className="font-mono text-xs text-muted-foreground">{prospect.meta_lead_id}</span>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Notes */}
              {hasActivityOrNotes && (
                <AccordionItem value="notes" className="border-none">
                  <AccordionTrigger className="py-2 text-sm hover:no-underline">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      Notes
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <NotesDisplay
                      activityLog={prospect.activity_log}
                      notes={prospect.notes}
                      qualificationNotes={prospect.qualification_notes}
                    />
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          )}

          {/* Workflow Actions - only show if not already closed/disqualified and not a viewer */}
          {!isViewer && !isClosedOrDisqualified && (
            <div className="flex gap-2 flex-wrap border-t pt-3">
              {/* Call Outcome buttons - for recording call results */}
              {canRecordCallOutcome && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickStatusChange('attempted')}
                    disabled={isUpdating}
                    className="h-8 text-orange-700 border-orange-300 hover:bg-orange-50"
                  >
                    No Answer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickStatusChange('contacted')}
                    disabled={isUpdating}
                    className="h-8 text-purple-700 border-purple-300 hover:bg-purple-50"
                  >
                    Callback
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickStatusChange('not_interested')}
                    disabled={isUpdating}
                    className="h-8 text-gray-600 border-gray-300 hover:bg-gray-50"
                  >
                    Not Interested
                  </Button>
                </>
              )}
              {canQualify && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStatusChange('qualified')}
                  disabled={isUpdating}
                  className="h-8"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Qualify
                </Button>
              )}
              {canMarkInterested && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStatusChange('interested')}
                  disabled={isUpdating}
                  className="h-8 text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                >
                  Interested
                </Button>
              )}
              {canMarkProposal && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStatusChange('proposal')}
                  disabled={isUpdating}
                  className="h-8"
                >
                  Proposal Sent
                </Button>
              )}
              {canClose && (
                <>
                  <Button
                    size="sm"
                    onClick={() => setShowCloseWon(true)}
                    className="bg-green-600 hover:bg-green-700 h-8"
                    disabled={isUpdating}
                  >
                    <DollarSign className="h-3 w-3 mr-1" />
                    Won
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowCloseLost(true)}
                    disabled={isUpdating}
                    className="h-8"
                  >
                    Lost
                  </Button>
                </>
              )}
              {hasBooking && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMeetingOutcome(true)}
                  disabled={isUpdating}
                  className="h-8 text-blue-700 border-blue-300 hover:bg-blue-50"
                >
                  <ClipboardList className="h-3 w-3 mr-1" />
                  Meeting Outcome
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDisqualify(true)}
                disabled={isUpdating}
                className="text-muted-foreground h-8"
              >
                <X className="h-3 w-3 mr-1" />
                Disqualify
              </Button>
            </div>
          )}
        </CardContent>
      )}

      {/* Modals */}
      <CloseWonModal
        open={showCloseWon}
        onClose={() => setShowCloseWon(false)}
        onConfirm={handleCloseWon}
        prospectName={displayName}
        isLoading={isUpdating}
      />
      <CloseLostModal
        open={showCloseLost}
        onClose={() => setShowCloseLost(false)}
        onConfirm={handleCloseLost}
        prospectName={displayName}
        isLoading={isUpdating}
      />
      <DisqualifyModal
        open={showDisqualify}
        onClose={() => setShowDisqualify(false)}
        onConfirm={handleDisqualify}
        prospectName={displayName}
        isLoading={isUpdating}
      />
      <MeetingOutcomeModal
        open={showMeetingOutcome}
        onClose={() => setShowMeetingOutcome(false)}
        onSave={handleMeetingOutcome}
        prospectName={displayName}
        isLoading={isUpdating}
        initialData={{
          setter: prospect.setter,
          attended_meeting: prospect.attended_meeting,
          no_show_answered_dial: prospect.no_show_answered_dial,
          no_show_no_answers: prospect.no_show_no_answers,
          qualification_label: prospect.qualification_label,
          next_steps: prospect.next_steps,
        }}
      />
    </Card>
  );
}

function InboundListSkeleton() {
  return (
    <div className="space-y-4 max-w-4xl">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Error state component
function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Card className="p-8 text-center">
      <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
      <h3 className="text-lg font-medium mb-2">Unable to Load Leads</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {error instanceof Error ? error.message : 'An unexpected error occurred while loading your inbound leads.'}
      </p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      )}
    </Card>
  );
}

// Empty state component
function EmptyState({ view }: { view: InboundView }) {
  const messages: Record<InboundView, { title: string; description: string }> = {
    upcoming: {
      title: 'No Upcoming Meetings',
      description: 'Leads with scheduled meetings will appear here.',
    },
    new: {
      title: 'No New Leads',
      description: 'New and qualified leads will appear here.',
    },
    follow_up: {
      title: 'No Follow-ups Needed',
      description: 'Leads that need follow-up will appear here.',
    },
    pipeline: {
      title: 'Pipeline Empty',
      description: 'Leads in proposal stage will appear here.',
    },
    closed: {
      title: 'No Closed Deals',
      description: 'Closed deals (won or lost) will appear here.',
    },
    all: {
      title: 'No Inbound Leads',
      description: 'Leads from website bookings and Meta ads will appear here.',
    },
  };

  const { title, description } = messages[view];

  return (
    <Card className="p-8 text-center">
      <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Card>
  );
}

// Sources that count as "inbound" leads (came to us, not cold outreach)
const INBOUND_SOURCES = ['inbound_booking', 'calcom_booking', 'meta_lead_ad', 'test_endpoint'];

export function InboundPage() {
  const hasDialer = useHasDialer();
  const isViewer = useIsViewer();
  const [currentView, setCurrentView] = useState<InboundView>('all');
  const [selectedProspect, setSelectedProspect] = useState<{
    id: string;
    phone: string;
    name?: string;
  } | null>(null);
  const [updatingProspectId, setUpdatingProspectId] = useState<string | null>(null);

  // Fetch all prospects and filter client-side by source
  const { data, isLoading, error, refetch } = useProspects({
    queue: 'all', // Get all, we'll filter by source
  });

  // Mutation for updating prospects
  const updateProspectMutation = useUpdateProspect();

  // Filter for inbound leads by source
  const inboundLeads = useMemo(() => {
    return (data?.prospects || []).filter(
      (p) => p.source && INBOUND_SOURCES.includes(p.source)
    );
  }, [data?.prospects]);

  // Calculate counts for each view
  const viewCounts = useMemo(() => {
    const counts: Record<InboundView, number> = {
      upcoming: 0,
      new: 0,
      follow_up: 0,
      pipeline: 0,
      closed: 0,
      all: inboundLeads.length,
    };

    const now = new Date();

    inboundLeads.forEach((lead) => {
      // Upcoming: has future meeting time
      if (lead.meeting_time && new Date(lead.meeting_time) >= now) {
        counts.upcoming++;
      }

      // Count by status
      const status = lead.status as ProspectStatus;
      if (VIEW_STATUSES.new?.includes(status)) counts.new++;
      if (VIEW_STATUSES.follow_up?.includes(status)) counts.follow_up++;
      if (VIEW_STATUSES.pipeline?.includes(status)) counts.pipeline++;
      if (VIEW_STATUSES.closed?.includes(status)) counts.closed++;
    });

    return counts;
  }, [inboundLeads]);

  // Filter and sort leads based on current view
  const filteredLeads = useMemo(() => {
    const now = new Date();
    let leads = [...inboundLeads];

    // Apply view-specific filters
    if (currentView === 'upcoming') {
      // Only leads with future meeting times
      leads = leads.filter((lead) => lead.meeting_time && new Date(lead.meeting_time) >= now);
      // Sort by meeting time (soonest first)
      leads.sort((a, b) => {
        return new Date(a.meeting_time!).getTime() - new Date(b.meeting_time!).getTime();
      });
    } else if (currentView !== 'all') {
      // Filter by status
      const allowedStatuses = VIEW_STATUSES[currentView];
      if (allowedStatuses) {
        leads = leads.filter((lead) => allowedStatuses.includes(lead.status as ProspectStatus));
      }
      // Sort by created_at (newest first)
      leads.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    } else {
      // All view: sort by meeting time if exists, then by created_at
      leads.sort((a, b) => {
        if (a.meeting_time && !b.meeting_time) return -1;
        if (!a.meeting_time && b.meeting_time) return 1;
        if (a.meeting_time && b.meeting_time) {
          return new Date(a.meeting_time).getTime() - new Date(b.meeting_time).getTime();
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    }

    return leads;
  }, [inboundLeads, currentView]);

  const handleCallProspect = (prospectId: string, phone: string, name?: string) => {
    setSelectedProspect({ id: prospectId, phone, name });
  };

  const handleStatusChange = async (
    prospectId: string,
    status: ProspectStatus,
    extra?: Record<string, unknown>
  ) => {
    const prospect = inboundLeads.find(p => p.id === prospectId);
    const prospectName = prospect?.name || prospect?.company_name || 'Lead';

    setUpdatingProspectId(prospectId);
    try {
      await updateProspectMutation.mutateAsync({
        id: prospectId,
        data: {
          status,
          ...extra,
        },
      });

      // Show success toast with appropriate message
      const statusLabel = STATUS_CONFIG[status]?.label || status;
      if (status === 'closed_won') {
        const amount = extra?.closed_amount as number;
        toast.success(`Deal closed! ${prospectName} - $${amount?.toLocaleString()}`);
      } else if (status === 'closed_lost') {
        toast.info(`Marked ${prospectName} as lost`);
      } else if (status === 'disqualified') {
        toast.info(`Disqualified ${prospectName}`);
      } else {
        toast.success(`Updated ${prospectName} to ${statusLabel}`);
      }
    } catch (err) {
      console.error('Failed to update prospect:', err);
      toast.error(`Failed to update ${prospectName}. Please try again.`);
    } finally {
      setUpdatingProspectId(null);
    }
  };

  return (
    <div className={`h-full flex flex-col ${hasDialer ? 'lg:flex-row' : ''}`}>
      {/* Left side - Inbound leads list */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="border-b px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Inbound Leads</h1>
              <p className="text-sm text-muted-foreground">
                Website bookings and ad campaigns
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="whitespace-nowrap">
                {filteredLeads.length} leads
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8">
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => {
                    exportProspectsCSV({ format: 'agency_leads' })
                      .then(() => toast.success('Agency Leads CSV downloaded'))
                      .catch((e) => toast.error(`Export failed: ${e.message}`));
                  }}>
                    Agency Leads CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    exportProspectsCSV({ format: 'agency_calls' })
                      .then(() => toast.success('Agency Calls CSV downloaded'))
                      .catch((e) => toast.error(`Export failed: ${e.message}`));
                  }}>
                    Agency Calls CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    exportProspectsCSV()
                      .then(() => toast.success('Full export downloaded'))
                      .catch((e) => toast.error(`Export failed: ${e.message}`));
                  }}>
                    Full Export
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* View Tabs - scrollable on mobile */}
          <Tabs value={currentView} onValueChange={(v) => setCurrentView(v as InboundView)}>
            <TabsList className="w-full h-auto flex-wrap sm:grid sm:grid-cols-6 gap-1">
              <TabsTrigger value="upcoming" className="text-xs flex-1 min-w-[80px]">
                Upcoming {viewCounts.upcoming > 0 && `(${viewCounts.upcoming})`}
              </TabsTrigger>
              <TabsTrigger value="new" className="text-xs flex-1 min-w-[60px]">
                New {viewCounts.new > 0 && `(${viewCounts.new})`}
              </TabsTrigger>
              <TabsTrigger value="follow_up" className="text-xs flex-1 min-w-[80px]">
                Follow-up {viewCounts.follow_up > 0 && `(${viewCounts.follow_up})`}
              </TabsTrigger>
              <TabsTrigger value="pipeline" className="text-xs flex-1 min-w-[70px]">
                Pipeline {viewCounts.pipeline > 0 && `(${viewCounts.pipeline})`}
              </TabsTrigger>
              <TabsTrigger value="closed" className="text-xs flex-1 min-w-[60px]">
                Closed {viewCounts.closed > 0 && `(${viewCounts.closed})`}
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs flex-1 min-w-[50px]">
                All {viewCounts.all > 0 && `(${viewCounts.all})`}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {isLoading ? (
            <InboundListSkeleton />
          ) : error ? (
            <ErrorState error={error} onRetry={() => refetch()} />
          ) : filteredLeads.length === 0 ? (
            <EmptyState view={currentView} />
          ) : (
            <div className="space-y-4 max-w-4xl">
              {filteredLeads.map((lead) => (
                <InboundLeadCard
                  key={lead.id}
                  prospect={lead}
                  onCall={hasDialer && !isViewer ? handleCallProspect : undefined}
                  onStatusChange={!isViewer ? handleStatusChange : undefined}
                  isSelected={selectedProspect?.id === lead.id}
                  isUpdating={updatingProspectId === lead.id}
                  isViewer={isViewer}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right side - Persistent dialer panel (hidden for CRM-only users) */}
      {hasDialer && (
        <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l bg-muted/30 flex flex-col max-h-[50vh] lg:max-h-none">
          <div className="border-b px-4 sm:px-6 py-4">
            <h2 className="text-lg font-semibold">Dialer</h2>
            <p className="text-xs text-muted-foreground">
              {selectedProspect
                ? `Ready to call ${selectedProspect.name || 'lead'}`
                : 'Click "Call" on a lead to start'}
            </p>
          </div>

          <div className="flex-1 overflow-auto p-4 sm:p-6">
            <Card className="p-4">
              <CallUI
                prefilledPhone={selectedProspect?.phone}
                prospectId={selectedProspect?.id}
                prospectName={selectedProspect?.name}
              />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
